/**
 * Per-playbook-step outcomes view for step-driven replay.
 *
 * @module cache-debug/CacheDebugPlaybookSteps
 */
import React from 'react';
import { Text } from '@chatsdet/ui';
import { CaretRight, Check, X, Minus } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { CacheDebugActionList } from './CacheDebugActionList';
import type {
  CacheDebugStepOutcome,
  CacheDebugCompiledEntry,
  CacheDebugPlaybookStep,
  CacheDebugAction,
} from './useCacheDebugData';

type Props = {
  playbookSteps: CacheDebugPlaybookStep[];
  stepOutcomes: CacheDebugStepOutcome[];
  compiledEntries: CacheDebugCompiledEntry[] | null;
  actions: CacheDebugAction[];
};

export function CacheDebugPlaybookSteps({
  playbookSteps,
  stepOutcomes,
  compiledEntries,
  actions,
}: Props) {
  const [expandedStep, setExpandedStep] = React.useState<number | null>(null);

  const outcomeByOrder = new Map(stepOutcomes.map((o) => [o.stepOrder, o]));
  const entryByOrder = compiledEntries
    ? new Map(compiledEntries.map((e) => [e.stepOrder, e]))
    : new Map<number, CacheDebugCompiledEntry>();

  return (
    <div className="space-y-0.5">
      <Text size="xs" weight="medium" className="text-muted-foreground mb-1">
        Playbook Steps
      </Text>
      {playbookSteps.map((pbStep) => {
        const outcome = outcomeByOrder.get(pbStep.order);
        const entry = entryByOrder.get(pbStep.order);
        const isExpanded = expandedStep === pbStep.order;

        // Get actions for this step if cached
        const stepActions =
          entry?.type === 'cached'
            ? entry.actionIndices
                .map((idx) => actions[idx])
                .filter((a): a is CacheDebugAction => a !== undefined)
            : [];

        const hasCachedActions = stepActions.length > 0;

        return (
          <div key={pbStep.order}>
            <button
              onClick={() => hasCachedActions && setExpandedStep(isExpanded ? null : pbStep.order)}
              className={`w-full flex items-center gap-2 py-1 px-2 rounded text-left text-xs ${
                hasCachedActions ? 'hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer' : 'cursor-default'
              }`}
            >
              {hasCachedActions ? (
                <motion.div
                  animate={{ rotate: isExpanded ? 90 : 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex-shrink-0"
                >
                  <CaretRight size={10} className="text-muted-foreground" />
                </motion.div>
              ) : (
                <span className="w-[10px]" />
              )}

              <OutcomeIcon outcome={outcome} />

              <span className="text-muted-foreground tabular-nums w-4">{pbStep.order}.</span>
              <span className="truncate flex-1">
                {pbStep.action} {pbStep.target}
              </span>

              <EntryTrustBadge entry={entry} />
              <OutcomeBadge outcome={outcome} entry={entry} actionCount={stepActions.length} />
            </button>

            <AnimatePresence>
              {isExpanded && hasCachedActions && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className="overflow-hidden pl-8"
                >
                  <div className="py-1">
                    <CacheDebugActionList actions={stepActions} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Shadow-phase per-entry trust (state · streak). Only rendered when the
 * entry carries trust bookkeeping (org has `cache.entry-trust` on).
 */
function EntryTrustBadge({ entry }: { entry: CacheDebugCompiledEntry | undefined }) {
  if (entry?.type !== 'cached' || !entry.trust) return null;
  const { state, streak } = entry.trust;

  const badgeClass =
    state === 'verified'
      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
      : state === 'suspect'
        ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-800/50 dark:text-gray-400';

  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${badgeClass}`}
      title={`Entry trust: ${state}, streak ${streak} (shadow — not yet used for routing)`}
    >
      {state} · {streak}
    </span>
  );
}

function OutcomeIcon({ outcome }: { outcome: CacheDebugStepOutcome | undefined }) {
  if (!outcome) return <Minus size={12} className="text-gray-400 flex-shrink-0" />;
  if (outcome.success) return <Check size={12} weight="bold" className="text-green-500 flex-shrink-0" />;
  if (outcome.recoveredVia) return <Check size={12} weight="bold" className="text-emerald-500 flex-shrink-0" />;
  return <X size={12} weight="bold" className="text-red-500 flex-shrink-0" />;
}

function OutcomeBadge({
  outcome,
  entry,
  actionCount,
}: {
  outcome: CacheDebugStepOutcome | undefined;
  entry: CacheDebugCompiledEntry | undefined;
  actionCount: number;
}) {
  if (!outcome && !entry) return null;

  const parts: string[] = [];

  if (entry?.type === 'gap' || outcome?.type === 'gap') {
    parts.push('L2 gap');
    if (outcome?.success) parts.push('solved');
    else if (outcome && !outcome.success) parts.push('failed');
  } else if (outcome?.recoveredVia === 'recovery') {
    parts.push('L2 recovery');
  } else if (outcome?.recoveredVia === 'gap') {
    parts.push('L2 gap');
  } else if (entry?.type === 'cached') {
    parts.push('cached');
    if (actionCount > 0) parts.push(`${actionCount} action${actionCount !== 1 ? 's' : ''}`);
  }

  if (parts.length === 0) return null;

  const isGap = entry?.type === 'gap' || outcome?.type === 'gap';
  const isRecovery = !!outcome?.recoveredVia;
  const isFailed = outcome && !outcome.success && !outcome.recoveredVia;

  const badgeClass = isFailed
    ? 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400'
    : isGap || isRecovery
      ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400'
      : 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400';

  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${badgeClass}`}>
      {parts.join(' · ')}
    </span>
  );
}
